import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form, Spinner } from 'react-bootstrap';
import { Building2, MapPin, Image as ImageIcon, Briefcase, Upload } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';

const setField = (setForm, field, value) => {
  setForm((prev) => ({ ...prev, [field]: value }));
};

const Field = ({ label, required, help, children }) => (
  <Form.Group className="mb-3">
    <Form.Label>
      {label}
      {required ? ' *' : ''}
    </Form.Label>
    {children}
    {help ? <Form.Text muted>{help}</Form.Text> : null}
  </Form.Group>
);

const SectionCard = ({ title, icon: Icon, children }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body>
      <div className="d-flex align-items-center gap-2 mb-3">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 36, height: 36, background: '#e8eef8', color: '#1e3a8a' }}
        >
          <Icon size={18} />
        </span>
        <h6 className="mb-0 fw-semibold">{title}</h6>
      </div>
      {children}
    </Card.Body>
  </Card>
);

const LogoUploader = ({ logoUrl, onUploaded }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      showToast(t('settings.storeProfile.toast.onlyImages'), 'warning');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast(t('settings.storeProfile.toast.tooLarge'), 'warning');
      return;
    }
    setUploading(true);
    try {
      const data = await api.uploadStoreLogo(file);
      onUploaded?.(data.logoUrl);
      showToast(t('settings.storeProfile.toast.uploaded'), 'success');
    } catch (err) {
      showToast(err.message || t('settings.storeProfile.toast.uploadFailed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  return (
    <div>
      <Form.Label>{t('settings.storeProfile.storeLogo')}</Form.Label>
      <div
        className={`q2c-logo-dropzone${dragging ? ' is-dragging' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={t('settings.storeProfile.logoAlt')} className="q2c-logo-preview" />
        ) : (
          <div className="text-center text-muted py-2">
            <Upload size={28} className="mb-2" />
            <div className="fw-semibold text-dark">{t('settings.storeProfile.uploadLogo')}</div>
          </div>
        )}
        <button
          type="button"
          className="q2c-logo-choose"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" /> {t('settings.storeProfile.uploading')}
            </>
          ) : (
            t('settings.storeProfile.chooseFile')
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif"
          hidden
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
      <Form.Text muted>{t('settings.storeProfile.logoHelp')}</Form.Text>
    </div>
  );
};

const StoreProfileTab = ({ form, setForm }) => {
  const { t } = useTranslation();
  return (
  <div className="row g-3">
    <div className="col-lg-6 d-flex flex-column gap-3">
      <SectionCard title={t('settings.storeProfile.companyInfo')} icon={Building2}>
        <Field
          label={t('settings.storeProfile.storeCurrency')}
          help={t('settings.storeProfile.storeCurrencyHelp')}
        >
          <Form.Control value={form.currency || '—'} readOnly plaintext className="fw-semibold" />
        </Field>
        <Field label={t('settings.storeProfile.companyName')} required help={t('settings.storeProfile.companyNameHelp')}>
          <Form.Control
            value={form.companyName || ''}
            onChange={(e) => setField(setForm, 'companyName', e.target.value)}
            placeholder={t('settings.storeProfile.companyNamePlaceholder')}
            required
          />
        </Field>
        <Field label={t('settings.storeProfile.supportEmail')} required help={t('settings.storeProfile.supportEmailHelp')}>
          <Form.Control
            type="email"
            value={form.supportEmail || ''}
            onChange={(e) => setField(setForm, 'supportEmail', e.target.value)}
            placeholder={t('settings.storeProfile.supportEmailPlaceholder')}
            required
          />
        </Field>
        <Field label={t('settings.storeProfile.phone')} required>
          <Form.Control
            value={form.phone || ''}
            onChange={(e) => setField(setForm, 'phone', e.target.value)}
            placeholder={t('settings.storeProfile.phonePlaceholder')}
            required
          />
        </Field>
        <Field label={t('settings.storeProfile.fax')}>
          <Form.Control
            value={form.fax || ''}
            onChange={(e) => setField(setForm, 'fax', e.target.value)}
            placeholder={t('settings.storeProfile.faxPlaceholder')}
          />
        </Field>
      </SectionCard>

      <SectionCard title={t('settings.storeProfile.businessAddress')} icon={MapPin}>
        <Field label={t('settings.storeProfile.street')} required>
          <Form.Control
            value={form.street || ''}
            onChange={(e) => setField(setForm, 'street', e.target.value)}
            placeholder={t('settings.storeProfile.streetPlaceholder')}
            required
          />
        </Field>
        <div className="row g-3">
          <div className="col-md-4">
            <Field label={t('settings.storeProfile.city')} required>
              <Form.Control
                value={form.city || ''}
                onChange={(e) => setField(setForm, 'city', e.target.value)}
                placeholder={t('settings.storeProfile.cityPlaceholder')}
                required
              />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label={t('settings.storeProfile.state')} required>
              <Form.Control
                value={form.state || ''}
                onChange={(e) => setField(setForm, 'state', e.target.value)}
                placeholder={t('settings.storeProfile.statePlaceholder')}
                required
              />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label={t('settings.storeProfile.zip')} required>
              <Form.Control
                value={form.zip || ''}
                onChange={(e) => setField(setForm, 'zip', e.target.value)}
                placeholder={t('settings.storeProfile.zipPlaceholder')}
                required
              />
            </Field>
          </div>
        </div>
      </SectionCard>
    </div>

    <div className="col-lg-6 d-flex flex-column gap-3">
      <SectionCard title={t('settings.storeProfile.companyLogo')} icon={ImageIcon}>
        <LogoUploader
          logoUrl={form.logoUrl}
          onUploaded={(logoUrl) => setField(setForm, 'logoUrl', logoUrl)}
        />
      </SectionCard>

      <SectionCard title={t('settings.storeProfile.businessDetails')} icon={Briefcase}>
        <Field
          label={t('settings.storeProfile.storeWebsite')}
          required
          help={t('settings.storeProfile.storeWebsiteHelp')}
        >
          <Form.Control
            value={form.storeWebsite || ''}
            onChange={(e) => setField(setForm, 'storeWebsite', e.target.value)}
            placeholder={t('settings.storeProfile.storeWebsitePlaceholder')}
            required
          />
        </Field>
        <Field
          label={t('settings.storeProfile.secureCartUrl')}
          help={t('settings.storeProfile.secureCartUrlHelp')}
        >
          <Form.Control
            value={form.secureCartUrl || ''}
            onChange={(e) => setField(setForm, 'secureCartUrl', e.target.value)}
            placeholder={t('settings.storeProfile.secureCartUrlPlaceholder')}
          />
        </Field>
        <Field label={t('settings.storeProfile.taxId')} help={t('settings.storeProfile.taxIdHelp')}>
          <Form.Control
            value={form.taxId || ''}
            onChange={(e) => setField(setForm, 'taxId', e.target.value)}
            placeholder={t('settings.storeProfile.taxIdPlaceholder')}
          />
        </Field>
        <Field label={t('settings.storeProfile.businessHours')} help={t('settings.storeProfile.businessHoursHelp')}>
          <Form.Control
            as="textarea"
            rows={4}
            value={form.businessHours || ''}
            onChange={(e) => setField(setForm, 'businessHours', e.target.value)}
            placeholder={'Monday-Friday: 9:00 AM - 6:00 PM\nSaturday: 10:00 AM - 4:00 PM\nSunday: Closed'}
          />
        </Field>
      </SectionCard>
    </div>
  </div>
  );
};

export default StoreProfileTab;
